import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Loader2, Plus, Trash, Truck, AlertTriangle, ScanBarcode, Sparkles } from "lucide-react";
import BarcodeScannerDialog from "./BarcodeScannerDialog";

const emptyLine = () => ({ _key: crypto.randomUUID(), sku: "", expected_quantity: "", received_quantity: "", note: "" });

function varianceInfo(expected, received) {
  const e = Number(expected) || 0;
  const r = Number(received) || 0;
  const v = r - e;
  if (v === 0) return { label: "OK", cls: "bg-emerald-100 text-emerald-800", value: 0 };
  if (v < 0) return { label: `${v} short`, cls: "bg-red-100 text-red-800", value: v };
  return { label: `+${v} over`, cls: "bg-amber-100 text-amber-900", value: v };
}

export default function SiteDeliveryDialog({ open, onOpenChange, project, onSuccess }) {
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [lines, setLines] = useState([emptyLine()]);
  const [busy, setBusy] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [flashKey, setFlashKey] = useState(null);
  const linesRef = useRef(lines);
  useEffect(() => { linesRef.current = lines; }, [lines]);

  useEffect(() => {
    if (open) {
      setReference("");
      setNote("");
      setLines([emptyLine()]);
      api.get("/inventory/items").then((r) => setCatalog(r.data)).catch(() => {});
    }
  }, [open]);

  const updateLine = (i, field, value) => {
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)));
  };

  const addLine = () => setLines((ls) => [...ls, emptyLine()]);
  const removeLine = (i) => setLines((ls) => ls.filter((_, idx) => idx !== i));

  const knownSku = (sku) =>
    !!catalog.find((c) => c.sku.toLowerCase() === (sku || "").trim().toLowerCase());

  const catalogLookup = (sku) =>
    catalog.find((c) => c.sku.toLowerCase() === (sku || "").trim().toLowerCase()) || null;

  const flash = (key) => {
    setFlashKey(key);
    setTimeout(() => setFlashKey((k) => (k === key ? null : k)), 900);
  };

  /**
   * Scanner handler — bumps Received on a matching existing line (any that has
   * a SKU column already filled) or appends a new flagged extra line.
   * Uses linesRef to avoid stale closures inside the continuous scanner.
   */
  const handleScanned = (code) => {
    const scanned = String(code || "").trim().toUpperCase();
    if (!scanned) return;
    const current = linesRef.current;
    const idx = current.findIndex((l) => l.sku.trim().toUpperCase() === scanned);
    if (idx >= 0) {
      const line = current[idx];
      const next = (Number(line.received_quantity) || 0) + 1;
      setLines((ls) =>
        ls.map((l, i) => (i === idx ? { ...l, received_quantity: String(next) } : l))
      );
      flash(line._key);
      const known = catalogLookup(scanned);
      toast.success(`+1 ${known?.name || scanned} · now ${next} received`);
    } else {
      // Reuse the last empty line if there is one, else append a new one
      const known = catalogLookup(scanned);
      const emptyIdx = current.findIndex((l) => !l.sku.trim());
      const newLine = {
        _key: crypto.randomUUID(),
        sku: scanned,
        expected_quantity: "",
        received_quantity: "1",
        note: known ? "" : "Not in catalog — verify",
      };
      if (emptyIdx >= 0) {
        setLines((ls) => ls.map((l, i) => (i === emptyIdx ? { ...newLine, _key: l._key } : l)));
        flash(current[emptyIdx]._key);
      } else {
        setLines((ls) => [...ls, newLine]);
        flash(newLine._key);
      }
      if (known) {
        toast.success(`Added ${known.name} · 1 received`);
      } else {
        toast.warning(`Unknown SKU ${scanned} — added and flagged`, {
          icon: <Sparkles size={14} />,
        });
      }
    }
  };

  const validLines = lines.filter((l) => l.sku.trim());

  const totals = validLines.reduce(
    (acc, l) => {
      const e = Number(l.expected_quantity) || 0;
      const r = Number(l.received_quantity) || 0;
      acc.expected += e;
      acc.received += r;
      const v = r - e;
      if (v < 0) acc.short += -v;
      if (v > 0) acc.over += v;
      if (!knownSku(l.sku)) acc.unknown += 1;
      return acc;
    },
    { expected: 0, received: 0, short: 0, over: 0, unknown: 0 }
  );

  const submit = async (e) => {
    e.preventDefault();
    if (validLines.length === 0) {
      toast.error("Add at least one line item");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        reference: reference.trim(),
        note: note.trim(),
        lines: validLines.map((l) => ({
          sku: l.sku.trim(),
          expected_quantity: Number(l.expected_quantity) || 0,
          received_quantity: Number(l.received_quantity) || 0,
          note: l.note || "",
        })),
      };
      await api.post(`/projects/${project.id}/deliveries`, payload);
      toast.success(`Logged delivery — ${totals.received} of ${totals.expected} received`);
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not log delivery");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl rounded-md" data-testid="site-delivery-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="grid place-items-center h-10 w-10 rounded-sm bg-blue-100 text-blue-800">
              <Truck size={20} />
            </div>
            <div>
              <DialogTitle className="font-display text-2xl font-black tracking-tight">
                Log Site Delivery
              </DialogTitle>
              <DialogDescription className="text-slate-600 mt-1">
                Materials delivered directly to <span className="font-semibold text-slate-900">{project?.name}</span> —
                type or <span className="font-semibold">scan</span> each SKU. Scans auto-increment
                Received; unknown SKUs are added and flagged. Warehouse stock is not affected.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">
                Reference / PO / BOL
              </Label>
              <Input
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="PO-8821"
                data-testid="delivery-reference-input"
                className="rounded-sm mt-1.5 font-mono"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Delivery Note</Label>
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Driver name, condition, etc."
                data-testid="delivery-note-input"
                className="rounded-sm mt-1.5"
              />
            </div>
          </div>

          <div className="border border-slate-200 rounded-md overflow-hidden">
            <div className="flex items-center justify-between bg-slate-50 border-b border-slate-200 p-3">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Line Items</div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  onClick={() => setScannerOpen(true)}
                  size="sm"
                  data-testid="delivery-scan-btn"
                  className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white"
                >
                  <ScanBarcode size={14} className="mr-1.5" /> Scan
                </Button>
                <Button
                  type="button"
                  onClick={addLine}
                  variant="outline"
                  size="sm"
                  data-testid="delivery-add-line-btn"
                  className="rounded-sm border-slate-300"
                >
                  <Plus size={14} className="mr-1.5" /> Add Line
                </Button>
              </div>
            </div>

            <div className="divide-y divide-slate-200 max-h-[45vh] overflow-y-auto">
              {lines.map((l, i) => {
                const v = varianceInfo(l.expected_quantity, l.received_quantity);
                const unknown = l.sku.trim() && !knownSku(l.sku);
                return (
                  <div
                    key={l._key}
                    className={`p-3 grid grid-cols-12 gap-2 items-end transition-colors ${flashKey === l._key ? "bg-emerald-50" : ""}`}
                    data-testid={`delivery-line-${i}`}
                  >
                    <div className="col-span-4">
                      {i === 0 && <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">SKU Code</Label>}
                      <Input
                        value={l.sku}
                        onChange={(e) => updateLine(i, "sku", e.target.value)}
                        placeholder="Type or scan"
                        list={`sku-datalist-${i}`}
                        data-testid={`delivery-sku-${i}`}
                        className="rounded-sm mt-1 font-mono uppercase"
                      />
                      <datalist id={`sku-datalist-${i}`}>
                        {catalog.map((c) => (
                          <option key={c.id} value={c.sku}>{c.name}</option>
                        ))}
                      </datalist>
                      {unknown && (
                        <div className="mt-1 flex items-center gap-1 text-[11px] text-amber-800">
                          <AlertTriangle size={11} /> Unknown SKU — will still be logged
                        </div>
                      )}
                    </div>
                    <div className="col-span-2">
                      {i === 0 && <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Expected</Label>}
                      <Input
                        type="number"
                        min="0"
                        value={l.expected_quantity}
                        onChange={(e) => updateLine(i, "expected_quantity", e.target.value)}
                        data-testid={`delivery-expected-${i}`}
                        className="rounded-sm mt-1 font-mono"
                      />
                    </div>
                    <div className="col-span-2">
                      {i === 0 && <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Received</Label>}
                      <Input
                        type="number"
                        min="0"
                        value={l.received_quantity}
                        onChange={(e) => updateLine(i, "received_quantity", e.target.value)}
                        data-testid={`delivery-received-${i}`}
                        className="rounded-sm mt-1 font-mono"
                      />
                    </div>
                    <div className="col-span-3">
                      {i === 0 && <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Variance</Label>}
                      <div className="mt-1 h-9 flex items-center">
                        <span className={`inline-flex items-center px-2 py-1 rounded-sm text-xs font-semibold font-mono ${v.cls}`} data-testid={`delivery-variance-${i}`}>
                          {v.label}
                        </span>
                      </div>
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeLine(i)}
                        disabled={lines.length === 1}
                        aria-label="Remove line"
                        data-testid={`delivery-remove-line-${i}`}
                        className="h-9 w-9 rounded-sm text-slate-500 hover:text-red-600"
                      >
                        <Trash size={16} />
                      </Button>
                    </div>
                    <div className="col-span-12">
                      <Input
                        value={l.note}
                        onChange={(e) => updateLine(i, "note", e.target.value)}
                        placeholder="Line note (damage, backorder, etc.)"
                        data-testid={`delivery-line-note-${i}`}
                        className="rounded-sm text-sm"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {validLines.length > 0 && (
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 grid grid-cols-4 gap-2 text-sm">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Expected</div>
                <div className="font-mono font-semibold text-slate-900">{totals.expected}</div>
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Received</div>
                <div className="font-mono font-semibold text-slate-900">{totals.received}</div>
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Short</div>
                <div className={`font-mono font-semibold ${totals.short > 0 ? "text-red-700" : "text-slate-900"}`}>{totals.short}</div>
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Over</div>
                <div className={`font-mono font-semibold ${totals.over > 0 ? "text-amber-800" : "text-slate-900"}`}>{totals.over}</div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy || validLines.length === 0}
              data-testid="delivery-submit-btn"
              className="bg-blue-800 hover:bg-blue-900 text-white rounded-sm"
            >
              {busy && <Loader2 size={16} className="animate-spin mr-1.5" />}
              <Truck size={16} className="mr-1.5" /> Log Delivery
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>

    <BarcodeScannerDialog
      open={scannerOpen}
      onOpenChange={setScannerOpen}
      onDetected={handleScanned}
      continuous={true}
      title="Scan site delivery"
    />
    </>
  );
}
